import { defineStore } from 'pinia'

import { useApiStore } from '@/stores/api'
import { useAppContextStore } from '@/stores/appContext'
import { useAuthenticationStore } from '@/stores/authentication'
import { useFeedStore } from '@/stores/feed'
import { useFormApplicationStore } from '@/stores/form/application'
import { useNotificationStore } from '@/stores/notification'
import { usePolicyStore } from '@/stores/policy'
import { useProtobufStore } from '@/stores/protobuf'
import { useSettingsStore } from '@/stores/settings'

import { api } from '@/api'
import router from '@/router'
import routeNames from '@/router/routeNames'
import { isAbortError } from '@/utils/api'
import { hotReloadStore } from '@/utils/build'

import type { User } from '@policyfly/protobuf/patrick'
import type { AxiosErrorCode } from 'types/api'

export interface Wording {
  text: string
  formatted: string
  context: Record<string, string>
  meta: Record<string, string | number | boolean>
}

export interface State {
  ready: boolean
  assignee: User | null
  owner: User | null
  lock_code: number | null
  wordings: Wording[]
}

export const useApplicationStore = defineStore('application', {
  state: (): State => ({
    ready: false,
    assignee: null,
    owner: null,
    lock_code: null,
    wordings: [],
  }),

  getters: {
    assigneeName: (state): string => state.assignee ? `${state.assignee.userDetail?.firstName} ${state.assignee.userDetail?.lastName}` : '',
    sanctionsCheckFailed: (state): boolean => !!state.lock_code,
    issuanceMessage (): string {
      const appContextStore = useAppContextStore()
      return appContextStore.kind === 'RENEWAL'
        ? 'This renewal has been issued.'
        : appContextStore.isCancellation
          ? 'This policy is now cancelled.'
          : 'This policy is now active.'
    },
    PNI (state): string {
      if (!state.ready) return 'Loading...'
      const protobufStore = useProtobufStore()
      const formApplicationStore = useFormApplicationStore()
      let PNI: string | null = ''
      let DBA: string | null = ''
      if (protobufStore.currentPolicyState.primaryNamedInsured) {
        PNI = protobufStore.currentPolicyState.primaryNamedInsured.legalName
        DBA = protobufStore.currentPolicyState.primaryNamedInsured.dba
      } else if (formApplicationStore.PNI) {
        return formApplicationStore.PNI
      } else {
        const appContextStore = useAppContextStore()
        const nestedResponsePayload = appContextStore.nestedResponsePayload
        if (nestedResponsePayload && nestedResponsePayload.get('PNI')) {
          PNI = nestedResponsePayload.get<string>('PNI')
          DBA = nestedResponsePayload.get<string>('DBA')
        }
      }
      if (!PNI) return ''
      return DBA ? `${PNI} DBA ${DBA}` : PNI
    },
  },

  actions: {
    /**
     * Fetches the application with the provided id and sets relevant state in other stores.
     * If an application fails to load, may redirect based on the reason for failure.
     */
    async fetchApplication (id: number, abortController?: AbortController): Promise<void> {
      const notificationStore = useNotificationStore()
      const apiStore = useApiStore()
      const protobufStore = useProtobufStore()
      const appContextStore = useAppContextStore()
      const policyStore = usePolicyStore()
      try {
        const { policyState, policy, json } = await apiStore.application.read(id, abortController)
        if (policyState) {
          protobufStore.policyState = policyState
          // @ts-expect-error(external): Swagger types do not match store
          policyStore.loadPolicy(policy!)
        }
        appContextStore.loadApplicationData(json)
      } catch (err) {
        if (isAbortError(err)) throw err
        const errorResponse = (err as AxiosErrorCode).response
        if (errorResponse?.status === 404) {
          notificationStore.snackbar.error('The application you requested could not be found.')
          await router.push({ name: routeNames.HOME })
        } else if (errorResponse?.status === 400 && errorResponse.data?.error?.code === 'PROGRAM_ID_MISMATCH') {
          notificationStore.snackbar.error({
            close: true,
            msg: 'That application does not belong to this program.',
            timeout: 0,
          })
          await router.push({ name: routeNames.HOME })
        } else {
          console.error(err)
        }
      }
    },
    async scan ({ data, file }: { data: unknown, file: Blob }) {
      const authenticationStore = useAuthenticationStore()
      const appContextStore = useAppContextStore()
      const formData = new FormData()
      formData.append('program', String(authenticationStore.programIds.id))
      formData.append('data', JSON.stringify(data))
      formData.append('fileData', file)
      const res = await api.applications.scan({ body: formData })
      const { Application } = res.data
      appContextStore.loadApplicationData(Application)
      this.ready = true
      return Application
    },
    async archive (id: number) {
      const apiStore = useApiStore()
      const appContextStore = useAppContextStore()
      await apiStore.application.archive(id)
      appContextStore.clearApplicationData()
    },
    async delete (id: number) {
      const apiStore = useApiStore()
      const appContextStore = useAppContextStore()
      await apiStore.application.delete(id)
      appContextStore.clearApplicationData()
    },
    async assign (assignee: User | false) {
      const appContextStore = useAppContextStore()
      const apiStore = useApiStore()
      const protobufStore = useProtobufStore()

      try {
        const id = appContextStore.loadedApplicationData.id!
        const { json, policyState, applicationFormData } = await apiStore.application.assign(id, assignee)

        if (assignee) {
          appContextStore.loadApplicationData(json)
          if (policyState) {
            protobufStore.loadData({ policyState, applicationFormData })
          }
        } else {
          this.assignee = null
        }
      } catch (err) {
        console.error(err)
      }
    },
    async refresh (id: number, abortController?: AbortController) {
      const apiStore = useApiStore()
      const appContextStore = useAppContextStore()
      const protobufStore = useProtobufStore()
      const settingsStore = useSettingsStore()
      const feedStore = useFeedStore()

      const freshness = await apiStore.application.freshness(id, abortController)
      if (settingsStore.protobuf) {
        if (freshness !== protobufStore.currentPolicyState.resourceVersion) {
          await this.fetchApplication(id, abortController)
          await feedStore.refresh()
        }
      } else {
        const current = appContextStore.loadedApplicationData.modified as number
        if (Date.parse(freshness) > current) {
          await this.fetchApplication(id, abortController)
          await feedStore.refresh()
        }
      }
    },
  },
})

hotReloadStore(useApplicationStore)
